import { NavLink, Outlet, useLocation, useNavigate } from "react-router"
import { Button } from "@repo/ui"
import { useTranslation } from "react-i18next"
import {
  ChartPieSlice,
  FileText,
  SignOut,
  CaretDown,
  Gear,
  Check,
  // HandCoins,
} from "@phosphor-icons/react"
import { memo, useCallback, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import SVGLogo from "@/assets/logo.svg"
import { useLogout } from "@/feature/auth/api"
import { useMetaData } from "@/lib/hooks/useMetaData"
import { useDebugModeStore } from "@/global/store"
import { Switch } from "@/components/ui/switch"
import { LayoutContainer } from "@/components/shared/Container"
import { PostHogPageView } from "@/feature/posthog/page-view"
import { Show } from "@/components/shared/show"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/feature/auth/store/authStore"
import { useSettingsStore } from "@/feature/settings/store/useOrganizationStore"
import { useFinancialModelSettingsStore } from "@/feature/financial-model/settings/store/financialModelSettingsStore"

const SettingsDropdown = () => {
  const organizationId = useSettingsStore((state) => state.organization?.id)
  const organizationName = useSettingsStore((state) => state.organization?.name)
  const email = useAuthStore((state) => state.user?.email)
  const workspaceName = useSettingsStore((state) => state.active_workspace.name)
  const location = useLocation()
  const { pathname } = location
  const [isOpen, setIsOpen] = useState(false)
  const logout = useLogout()
  const navigate = useNavigate()
  const handleLogout = useCallback(() => {
    logout()
    navigate(`/auth/login`)
  }, [logout, navigate])
  const { t } = useTranslation(`common`)
  if (!organizationId) return null

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <li className="flex items-center text-xs text-white w-full justify-between px-3">
          {organizationName ?? `Organization`}
          <CaretDown
            size={16}
            className={cn(
              `transition-transform duration-300`,
              isOpen && `rotate-180`
            )}
          />
        </li>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="pt-2 px-3 pb-3">
        <DropdownMenuLabel className="px-0">
          {organizationName ?? `Organization`}
        </DropdownMenuLabel>
        <p className="text-xs text-secondary">Launch Plan</p>
        <NavLink
          to="/dashboard/settings"
          onClick={() => setIsOpen(false)}
          state={{ from: pathname }}
        >
          <Button
            variant="secondary"
            className="w-full justify-center items-center gap-1 my-4"
            size="sm"
          >
            <Gear size={16} />
            {t(`layout.sidebar.settings`)}
          </Button>
        </NavLink>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="flex flex-col gap-2 my-4 ">
          <p className="text-xs text-secondary">{email}</p>
          <Show when={!!workspaceName}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs">{workspaceName}</p>
              <Check size={16} />
            </div>
          </Show>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="mb-4" />
        <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const Sidebar = memo(() => {
  const debugMode = useDebugModeStore()
  const { t } = useTranslation(`common`)
  const { pathname } = useLocation()
  const logout = useLogout()
  const navigate = useNavigate()
  const handleLogout = useCallback(() => {
    logout()
    navigate(`/auth/login`)
  }, [logout, navigate])

  const metaTitle = useMetaData()

  const hasFinancialModel = useFinancialModelSettingsStore(
    (state) => Object.keys(state.settings).length > 0
  )

  return (
    <>
      <div className="w-full max-w-[186px] bg-black h-screen">
        <div className="h-2" />
        <div className="flex flex-col h-full">
          <ul className="pt-4">
            <NavLink to="/">
              <li className="px-4">
                <img width={20} height={20} src={SVGLogo} alt="logo" />
              </li>
            </NavLink>
            <div className="mt-11 w-full flex justify-center">
              <SettingsDropdown />
            </div>
            <div className="mt-4">
              <NavigationItem to="/dashboard" pathname={pathname}>
                {({ isActive }) => (
                  <li className="flex items-center gap-3">
                    <ChartPieSlice
                      weight={isActive ? `fill` : `bold`}
                      size={16}
                    />
                    {t(`layout.sidebar.model`)}
                  </li>
                )}
              </NavigationItem>
            </div>

            {/* <div className="mt-4">
            <NavigationItem to="/dashboard/market" disabled>
            {({ isActive }) => (
              <>
              <Graph weight={isActive ? `fill` : `bold`} size={16} />
              {t(`layout.sidebar.market`)}
              </>
              )}
              </NavigationItem>
              </div> */}
            <div className="mt-2">
              <NavigationItem
                to="/dashboard/report"
                pathname={pathname}
                disabled={!hasFinancialModel}
              >
                {({ isActive }) => (
                  <li className="flex items-center gap-3">
                    <FileText weight={isActive ? `fill` : `bold`} size={16} />
                    {t(`layout.sidebar.report`)}
                  </li>
                )}
              </NavigationItem>
            </div>
            {/* <div className="mt-2">
              <NavigationItem
                to="/dashboard/fundraising"
                pathname={pathname}
                disabled={!hasFinancialModel}
              >
                {({ isActive }) => (
                  <li className="flex items-center gap-3">
                    <HandCoins weight={isActive ? `fill` : `bold`} size={16} />
                    {t(`layout.sidebar.fundraising`)}
                  </li>
                )}
              </NavigationItem>
            </div> */}
          </ul>
          <div className="mt-auto mb-4">
            <Show when={import.meta.env.VITE_DISABLE_DEBUG_MODE !== `true`}>
              <div className="flex items-center mb-4 gap-2 px-8">
                <span className="text-sm text-white">Test Mode</span>
                <Switch
                  checked={debugMode.mode === `test`}
                  onCheckedChange={() => {
                    debugMode.setMode(
                      debugMode.mode === `test` ? `production` : `test`
                    )
                  }}
                />
              </div>
            </Show>

            <hr className="myt-2 border-white/20" />
            <div className="px-4">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-secondary hover:text-white hover:bg-transparent"
              >
                <SignOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
      <title>{metaTitle.title}</title>
      <PostHogPageView />
    </>
  )
})

const INDIPENDENT_ROUTES = [
  `/dashboard/fundraising`,
  `/dashboard/report`,
  `/dashboard/workspace`,
]

const NavigationItem = (props: {
  to: string
  children: ({ isActive }: { isActive: boolean }) => React.ReactNode
  disabled?: boolean
  pathname: string
}) => {
  const isEqual = useMemo(() => {
    const isIndependent = INDIPENDENT_ROUTES.find((route) =>
      props.to.startsWith(route)
    )
    if (isIndependent) {
      return props.pathname.includes(props.to)
    }

    return (
      !INDIPENDENT_ROUTES.some((route) => props.pathname.startsWith(route)) &&
      props.pathname.includes(props.to)
    )
  }, [props.pathname, props.to])
  return (
    <NavLink
      to={props.to}
      className={cn(
        isEqual ? `hover:font-medium bg-gray-700` : `text-white`,
        `p-4 rounded-lg h-12 flex items-center gap-3 hover:font-medium hover:bg-gray-700 max-w-[154px] w-full mx-auto text-white text-xs`,
        props.disabled && `opacity-50 cursor-not-allowed`
      )}
      onClick={(e) => {
        if (props.disabled) {
          e.preventDefault()
        }
      }}
    >
      {props.children({ isActive: isEqual })}
    </NavLink>
  )
}

type AppLayoutProps =
  | {
      withChildren?: true
      children?: React.ReactNode
    }
  | {
      withChildren?: false
      children?: never
    }

export const AppLayout = memo((props: AppLayoutProps) => (
  <div className="flex overflow-hidden">
    <Sidebar />
    <LayoutContainer>
      {props.withChildren ? props.children : <Outlet />}
    </LayoutContainer>
  </div>
))

AppLayout.displayName = `AppLayout`
